/**
 * Utility functions to detect, preserve, and convert tables from various formats
 * (HTML, Excel/Sheets TSV, space-aligned PDF text, missing-delimiter pipe tables)
 * into standard GitHub-Flavored Markdown (GFM) tables.
 */

/**
 * Converts any HTML <table> elements into GFM Markdown table strings.
 * Works with tables from Word, Excel, Google Docs, Web pages, and rich PDF viewers,
 * including tables without <thead> or <th> elements.
 */
export const convertHtmlTablesToMarkdown = (html) => {
  if (!html || typeof html !== "string" || !/<table[\s>]/i.test(html)) {
    return html;
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const tables = Array.from(doc.querySelectorAll("table"));

    if (!tables.length) return html;

    tables.forEach((table) => {
      const rows = Array.from(table.rows || table.querySelectorAll("tr"));
      if (rows.length === 0) return;

      const matrix = [];
      rows.forEach((tr) => {
        const cells = Array.from(tr.cells || tr.querySelectorAll("th, td"));
        if (cells.length > 0) {
          const rowValues = cells.map((cell) => {
            // Extract text while preserving spacing and removing newlines
            let cellText = cell.innerHTML
              ? cell.innerHTML
                  .replace(/<br\s*\/?>/gi, " ")
                  .replace(/<p[^>]*>/gi, "")
                  .replace(/<\/p>/gi, " ")
                  .replace(/<[^>]+>/g, "")
              : cell.textContent || "";

            return cellText
              .replace(/&nbsp;/g, " ")
              .replace(/\r?\n+/g, " ")
              .replace(/\|/g, "\\|")
              .trim();
          });
          matrix.push(rowValues);
        }
      });

      if (matrix.length === 0) return;

      const maxCols = Math.max(...matrix.map((r) => r.length), 1);
      if (maxCols === 0) return;

      // Ensure every row has maxCols
      const normalized = matrix.map((row) => {
        const copy = [...row];
        while (copy.length < maxCols) copy.push("");
        return copy;
      });

      const headerRow = normalized[0];
      const delimiterRow = Array(maxCols).fill("---");
      const bodyRows = normalized.slice(1);

      const tableLines = [
        `| ${headerRow.join(" | ")} |`,
        `| ${delimiterRow.join(" | ")} |`,
      ];

      if (bodyRows.length > 0) {
        bodyRows.forEach((r) => {
          tableLines.push(`| ${r.join(" | ")} |`);
        });
      }

      const mdTable = `\n\n${tableLines.join("\n")}\n\n`;
      const placeholder = doc.createTextNode(mdTable);
      table.parentNode?.replaceChild(placeholder, table);
    });

    return doc.body.innerHTML || html;
  } catch (_e) {
    return html;
  }
};

/**
 * Checks if a string contains Tab-Separated Values (TSV) like Excel/Sheets copies.
 */
export const isTsvContent = (text) => {
  if (!text || typeof text !== "string") return false;
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length < 2) return false;
  const tabLines = lines.filter((l) => l.includes("\t"));
  return tabLines.length >= 2 && tabLines.length >= lines.length * 0.4;
};

/**
 * Converts a TSV string into a GFM Markdown table.
 */
export const convertTsvToMarkdownTable = (text) => {
  if (!text || typeof text !== "string") return text;
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length < 2) return text;

  const matrix = lines.map((line) =>
    line.split("\t").map((c) =>
      c
        .replace(/&nbsp;/g, " ")
        .replace(/\r?\n+/g, " ")
        .replace(/\|/g, "\\|")
        .trim()
    )
  );

  const maxCols = Math.max(...matrix.map((r) => r.length), 1);
  if (maxCols < 2) return text;

  const normalized = matrix.map((row) => {
    const copy = [...row];
    while (copy.length < maxCols) copy.push("");
    return copy;
  });

  const headerRow = normalized[0];
  const delimiterRow = Array(maxCols).fill("---");
  const bodyRows = normalized.slice(1);

  const tableLines = [
    `| ${headerRow.join(" | ")} |`,
    `| ${delimiterRow.join(" | ")} |`,
    ...bodyRows.map((r) => `| ${r.join(" | ")} |`),
  ];

  return tableLines.join("\n");
};

/**
 * Converts contiguous blocks of tab-separated lines within a mixed text into GFM tables.
 */
export const convertTsvBlocksInText = (text) => {
  if (!text || typeof text !== "string" || !text.includes("\t")) return text;

  const rawLines = text.split(/\r?\n/);
  const result = [];
  let tsvBuffer = [];

  const flushTsv = () => {
    if (tsvBuffer.length === 0) return;
    if (tsvBuffer.length >= 2 && tsvBuffer.some((l) => l.includes("\t"))) {
      const converted = convertTsvToMarkdownTable(tsvBuffer.join("\n"));
      result.push(converted);
    } else {
      result.push(...tsvBuffer);
    }
    tsvBuffer = [];
  };

  for (let i = 0; i < rawLines.length; i++) {
    const line = rawLines[i];
    if (line.includes("\t")) {
      tsvBuffer.push(line);
    } else if (tsvBuffer.length > 0 && line.trim() === "") {
      flushTsv();
      result.push(line);
    } else if (tsvBuffer.length > 0) {
      flushTsv();
      result.push(line);
    } else {
      result.push(line);
    }
  }
  flushTsv();

  return result.join("\n");
};

/**
 * Detects, aligns, and normalizes GitHub Flavored Markdown (GFM) pipe tables:
 * - Ensures delimiter row exists (| --- | --- |)
 * - Guarantees all rows start and end with pipes (| cell | cell |)
 * - Preserves spacing and cell values without mangling with backslashes
 * - Guarantees double-line separation before and after the table block so ReactMarkdown renders it cleanly
 */
export const normalizeAndFormatMarkdownTables = (text) => {
  if (!text || typeof text !== "string") return text;
  const rawLines = text.split(/\r?\n/);
  const result = [];
  let tableBlock = [];

  const isTableLine = (line) => {
    const trimmed = line.trim();
    if (!trimmed.includes("|")) return false;
    const pipeCount = (trimmed.match(/\|/g) || []).length;
    return pipeCount >= 1;
  };

  const flushBlock = () => {
    if (tableBlock.length === 0) return;
    if (tableBlock.length >= 2) {
      // Find delimiter index if already present
      let delimIdx = tableBlock.findIndex((l) => {
        const t = l.trim();
        return /^\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?$/.test(t);
      });

      // Split each row into cell values
      const rowCells = tableBlock.map((line) => {
        const trimmed = line.trim();
        let cells =
          trimmed.startsWith("|") && trimmed.endsWith("|")
            ? trimmed.slice(1, -1).split("|")
            : trimmed.split("|");
        return cells.map((c) => c.trim());
      });

      // If no delimiter row is present, insert one after the first row
      if (delimIdx === -1) {
        const colCount = Math.max(...rowCells.map((r) => r.length), 1);
        const delimiter = Array(colCount).fill("---");
        rowCells.splice(1, 0, delimiter);
        delimIdx = 1;
      }

      const maxCols = Math.max(...rowCells.map((r) => r.length), 1);

      // Normalize row cell counts and delimiters
      const normalizedRows = rowCells.map((cells, idx) => {
        const copy = [...cells];
        while (copy.length < maxCols) copy.push("");
        if (idx === delimIdx) {
          return (
            "| " +
            copy
              .map((c) =>
                c.startsWith(":") && c.endsWith(":")
                  ? ":---:"
                  : c.endsWith(":")
                  ? "---:"
                  : c.startsWith(":")
                  ? ":---"
                  : "---"
              )
              .join(" | ") +
            " |"
          );
        }
        return "| " + copy.join(" | ") + " |";
      });

      const mdTable = `\n\n${normalizedRows.join("\n")}\n\n`;
      result.push(mdTable);
    } else {
      result.push(...tableBlock);
    }
    tableBlock = [];
  };

  for (let i = 0; i < rawLines.length; i++) {
    const line = rawLines[i];
    if (isTableLine(line)) {
      tableBlock.push(line);
    } else {
      flushBlock();
      result.push(line);
    }
  }
  flushBlock();

  return result.join("\n").replace(/\n{3,}/g, "\n\n").trim();
};

/**
 * Detects blocks of lines where columns are aligned by 2 or more spaces
 * (common when extracting plain text from PDF documents) and converts them into GFM tables.
 * CRITICAL: Must NEVER touch lines that already contain pipe (|) delimiters!
 */
export const convertMultiSpaceTablesInText = (text) => {
  if (!text || typeof text !== "string") return text;
  const rawLines = text.split(/\r?\n/);
  const result = [];
  let tableBuffer = [];

  const flushTable = () => {
    if (tableBuffer.length === 0) return;
    if (tableBuffer.length >= 2) {
      const parsedRows = tableBuffer.map((line) =>
        line
          .split(/\s{2,}/)
          .map((c) => c.trim().replace(/\|/g, "\\|"))
          .filter(Boolean)
      );
      const colCounts = parsedRows.map((r) => r.length);
      const minCols = Math.min(...colCounts);
      const maxCols = Math.max(...colCounts);

      // Require at least 2 columns and consistent column count across rows
      if (minCols >= 2 && maxCols - minCols <= 1) {
        const normalized = parsedRows.map((row) => {
          const copy = [...row];
          while (copy.length < maxCols) copy.push("");
          return copy;
        });

        const headerRow = normalized[0];
        const delimiterRow = Array(maxCols).fill("---");
        const bodyRows = normalized.slice(1);

        const mdTable = [
          `\n\n| ${headerRow.join(" | ")} |`,
          `| ${delimiterRow.join(" | ")} |`,
          ...bodyRows.map((r) => `| ${r.join(" | ")} |`),
          `\n`,
        ].join("\n");

        result.push(mdTable);
        tableBuffer = [];
        return;
      }
    }
    result.push(...tableBuffer);
    tableBuffer = [];
  };

  for (let i = 0; i < rawLines.length; i++) {
    const line = rawLines[i];
    const trimmed = line.trim();

    const isMultiColumn =
      trimmed.length > 0 &&
      !trimmed.includes("|") &&
      !trimmed.startsWith("#") &&
      !trimmed.startsWith("- ") &&
      !trimmed.startsWith("* ") &&
      !trimmed.startsWith(">") &&
      !/^\d+\.\s/.test(trimmed) &&
      /\s{2,}/.test(trimmed) &&
      trimmed.split(/\s{2,}/).filter(Boolean).length >= 2;

    if (isMultiColumn) {
      tableBuffer.push(trimmed);
    } else {
      flushTable();
      result.push(line);
    }
  }
  flushTable();

  return result.join("\n");
};

/**
 * Fixes pipe tables that are missing the GFM delimiter row (| --- | --- |).
 */
export const fixMissingTableDelimiters = (text) => {
  return normalizeAndFormatMarkdownTables(text);
};

/**
 * Detects unstructured raw text tables typically produced by PDF text extractors
 * (where rows have labels followed by numeric or status data tokens)
 * and reconstructs them into GFM Markdown tables.
 */
export const convertExtractedPdfTablesToMarkdown = (text) => {
  if (!text || typeof text !== "string") return text;

  const lines = text.split("\n");
  const result = [];
  let i = 0;

  const isDataToken = (token) => {
    if (!token) return false;
    // Numbers, decimals, percentages, currency, ratios, brackets
    if (/^[£$€]?\(?-?\d+(?:[\.,/]\d+)*\)?%?$/.test(token)) return true;
    // Dashes or empty indicators
    if (/^[-–—]+$/.test(token)) return true;
    // Yes/No/True/False
    if (/^(?:Yes|No|True|False|Won|Completed|Entered)$/i.test(token)) return true;
    // Grades or single-letter codes: A, B, C, A, B
    if (/^[A-E](?:,\s*[A-E])*$/.test(token)) return true;
    return false;
  };

  const parseTrailingTokens = (rawLine) => {
    let clean = rawLine.trim().replace(/^#{1,6}\s+/, "");
    if (!clean || clean.startsWith("Table ") || clean.startsWith("|")) return null;

    if (/\((?:TH|TD)\)/i.test(clean)) {
      const parts = clean.split(/(?<=\))\s+/).map((s) => s.trim()).filter(Boolean);
      if (parts.length >= 2) {
        return { isCellMarker: true, cells: parts };
      }
    }

    const words = clean.split(/\s+/);
    if (words.length < 2) return null;

    let dataCount = 0;
    for (let idx = words.length - 1; idx >= 1; idx--) {
      if (isDataToken(words[idx])) {
        dataCount++;
      } else {
        break;
      }
    }

    if (dataCount >= 1) {
      const label = words.slice(0, words.length - dataCount).join(" ");
      const data = words.slice(words.length - dataCount);
      return { isCellMarker: false, label, data, colCount: dataCount };
    }

    return null;
  };

  while (i < lines.length) {
    const line = lines[i];
    const parsed = parseTrailingTokens(line);

    // Case 1: Lines with (TH) / (TD) markers (like Table 1)
    if (parsed && parsed.isCellMarker) {
      const markerBlock = [parsed.cells];
      let j = i + 1;
      while (j < lines.length) {
        const nextParsed = parseTrailingTokens(lines[j]);
        if (nextParsed && nextParsed.isCellMarker && nextParsed.cells.length === parsed.cells.length) {
          markerBlock.push(nextParsed.cells);
          j++;
        } else {
          break;
        }
      }
      if (markerBlock.length >= 2) {
        const cols = markerBlock[0].length;
        const header = markerBlock[0];
        const rows = markerBlock.slice(1);
        const md = [
          `| ${header.join(" | ")} |`,
          `| ${Array(cols).fill("---").join(" | ")} |`,
          ...rows.map((r) => `| ${r.join(" | ")} |`),
        ].join("\n");
        result.push(md);
        i = j;
        continue;
      }
    }

    // Case 2: Consecutive lines with matching number of trailing data tokens
    if (parsed && !parsed.isCellMarker && parsed.colCount >= 1) {
      const targetCols = parsed.colCount;
      const dataRows = [];
      let prevHeaderLine = null;

      if (result.length > 0) {
        const lastResultLine = result[result.length - 1].trim().replace(/^#{1,6}\s+/, "");
        if (lastResultLine && !lastResultLine.startsWith("|") && !lastResultLine.startsWith("Table ")) {
          const headerWords = lastResultLine.split(/\s+/);
          if (headerWords.length >= targetCols + 1) {
            const hLabel = headerWords.slice(0, headerWords.length - targetCols).join(" ");
            const hCols = headerWords.slice(headerWords.length - targetCols);
            prevHeaderLine = [hLabel, ...hCols];
          }
        }
      }

      let j = i;
      while (j < lines.length) {
        if (lines[j].trim() === "") {
          if (j + 1 < lines.length && parseTrailingTokens(lines[j + 1])?.colCount === targetCols) {
            j++;
            continue;
          } else {
            break;
          }
        }
        const p = parseTrailingTokens(lines[j]);
        if (p && !p.isCellMarker && p.colCount === targetCols) {
          dataRows.push([p.label, ...p.data]);
          j++;
        } else {
          break;
        }
      }

      if (dataRows.length >= 2 || (dataRows.length >= 1 && prevHeaderLine)) {
        let headers;
        let body;
        if (prevHeaderLine) {
          result.pop();
          headers = prevHeaderLine;
          body = dataRows;
        } else {
          headers = dataRows[0];
          body = dataRows.slice(1);
        }

        const totalCols = targetCols + 1;
        const md = [
          `| ${headers.join(" | ")} |`,
          `| ${Array(totalCols).fill("---").join(" | ")} |`,
          ...body.map((r) => `| ${r.join(" | ")} |`),
        ].join("\n");
        result.push(md);
        i = j;
        continue;
      }
    }

    result.push(line);
    i++;
  }

  return result.join("\n");
};

/**
 * Master function to detect and format tables across any text or HTML input:
 * 1. HTML tables (from Word, Excel, Web, rich PDF viewers) -> Markdown tables
 * 2. TSV blocks (from Excel, Google Sheets, tabbed PDF text) -> Markdown tables
 * 3. Space-aligned multi-column tables (from PDF text extraction) -> Markdown tables
 * 4. Raw text unstructured PDF tables -> Markdown tables
 * 5. Incomplete pipe tables -> adds GFM delimiters
 */
export const processTableContent = (content) => {
  if (!content || typeof content !== "string") return content;
  let text = content;

  // 1. Convert HTML tables if present
  if (/<table[\s>]/i.test(text)) {
    text = convertHtmlTablesToMarkdown(text);
  }

  // 2. Convert TSV blocks
  text = convertTsvBlocksInText(text);

  // 3. Format, align, and protect any existing pipe tables
  text = normalizeAndFormatMarkdownTables(text);

  // 4. Convert multi-space aligned tables (only applies to non-pipe lines)
  text = convertMultiSpaceTablesInText(text);

  // 5. Convert unstructured PDF extracted tables (numeric / status columns)
  text = convertExtractedPdfTablesToMarkdown(text);

  // 6. Final normalization pass ensuring standard GFM tables and spacing
  text = normalizeAndFormatMarkdownTables(text);

  return text;
};
